import React, { useState, useEffect } from "react";
import { message, Input, Select, Button, Tag, Empty } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import TableContainer from "../../components/TableContainer";
import { useDispatch, useSelector } from "react-redux";
import Smcs_Loader from "../../assests/images/loader-new.gif";
import { getReportData } from "../../actionsMethods/hubReconciliationMethods";
import { getToken, getPremiseId } from "../../utils/worker";
import { getApiMessage } from "./apiMessage";
import {
  STATUS_OPTIONS,
  STATUS_TAG,
  EMPTY_COUNTS,
  extractShipmentsPayload,
} from "./hubReconciliationConstants";
import {
  downloadReconciliationReport,
  getDownloadErrorMessage,
} from "./downloadReconciliationReport";
import "./styles.scss";

const HubReconciliationReportView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const reconcileId = location.state?.reconcileId || searchParams.get("reconcileId");

  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({ ...EMPTY_COUNTS });
  const [statusFilter, setStatusFilter] = useState([]);
  const [awbNumber, setAwbNumber] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({ status: [], awbNumber: "" });
  const [downloading, setDownloading] = useState(false);

  const {
    fetching: isFetching,
    result: reportData,
    error: isError,
  } = useSelector((state) => state.hubReconciliationReducer.getReportData);

  const fetchReportData = (filters = {}) => {
    const tokenStr = getToken();
    const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;
    const premiseIdStr = getPremiseId();
    const premiseId = premiseIdStr?.startsWith("Bearer ") ? premiseIdStr.split("Bearer ")[1] : premiseIdStr;
    const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
    const userId = userInfoData?.userInfo?.userId ? userInfoData.userInfo.userId.toString() : "";

    const nextStatus = filters.status || [];
    const nextAwb = (filters.awbNumber || "").trim();

    setAppliedFilters({ status: nextStatus, awbNumber: nextAwb });

    dispatch(
      getReportData({
        reconcileId,
        token,
        premiseId,
        userId,
        status: nextStatus.length ? nextStatus : undefined,
        awbNumber: nextAwb || undefined,
      })
    );
  };

  useEffect(() => {
    if (reconcileId) {
      fetchReportData();
    } else {
      message.error("Invalid Reconcile ID", 1);
    }
  }, [reconcileId]);

  useEffect(() => {
    if (reportData) {
      const { shipments, counts: nextCounts } = extractShipmentsPayload(reportData);
      setData(shipments);
      setCounts(nextCounts);
    } else if (isError) {
      message.error(getApiMessage(isError, "Failed to load Report data"), 3);
    }
  }, [isError, reportData]);

  const handleApplyFilters = () => {
    fetchReportData({
      status: statusFilter,
      awbNumber,
    });
  };

  const handleClearFilters = () => {
    setStatusFilter([]);
    setAwbNumber("");
    fetchReportData({ status: [], awbNumber: "" });
  };

  const handleDownload = async () => {
    if (!reconcileId) return;
    setDownloading(true);
    try {
      const fileName = await downloadReconciliationReport(reconcileId);
      message.success(`Report downloaded: ${fileName}`, 2);
    } catch (error) {
      const errorMsg = await getDownloadErrorMessage(error);
      message.error(errorMsg, 3);
    } finally {
      setDownloading(false);
    }
  };

  const handleStatusCardClick = (status) => {
    if (status === "ALL") {
      setStatusFilter([]);
      fetchReportData({ status: [], awbNumber });
      return;
    }

    const exists = statusFilter.includes(status);
    const next = exists
      ? statusFilter.filter((s) => s !== status)
      : [...statusFilter, status];
    setStatusFilter(next);
    fetchReportData({ status: next, awbNumber });
  };

  const removeAppliedStatus = (status) => {
    const next = statusFilter.filter((s) => s !== status);
    setStatusFilter(next);
    fetchReportData({ status: next, awbNumber });
  };

  const removeAppliedAwb = () => {
    setAwbNumber("");
    fetchReportData({ status: statusFilter, awbNumber: "" });
  };

  const hasActiveFilters =
    appliedFilters.status.length > 0 || Boolean(appliedFilters.awbNumber);

  const columns = [
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      render: (text) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline", fontWeight: 500 }}
          onClick={() => window.open(`/shipment-tracker?awb=${text}`, "_blank")}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const cfg = STATUS_TAG[status] || { color: "default", label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Scanned By",
      dataIndex: "scannedBy",
      key: "scannedBy",
      render: (text) => text || "—",
    },
    {
      title: "Scanned Time",
      dataIndex: "scannedTime",
      key: "scannedTime",
      render: (text) => text || "—",
    },
    {
      title: "Premise Name",
      dataIndex: "premiseName",
      key: "premiseName",
      render: (text) => text || "—",
    },
    {
      title: "Reconcile ID",
      dataIndex: "reconcileId",
      key: "reconcileId",
    },
  ];

  return (
    <div className="dashboard-container hub-recon" style={{ padding: "20px" }}>
      <div className="hub-recon__header">
        <div>
          <div className="hub-recon__title">
            <ArrowLeftOutlined
              className="hub-recon__back"
              onClick={() => navigate(-1)}
            />
            Reconciliation Report
          </div>
          <div className="hub-recon__subtitle">
            Reconcile ID: <strong>{reconcileId || "—"}</strong>
            {" · "}
            Click a status card below to filter the list
          </div>
        </div>
        <Button
          type="primary"
          className="save-btn hub-recon__btn"
          icon={<DownloadOutlined />}
          loading={downloading}
          onClick={handleDownload}
        >
          Download CSV
        </Button>
      </div>

      <div className="master-main-container view-all-staff">
        <div className="hub-recon__panel">
          <div className="hub-recon__section-label">
            <FilterOutlined style={{ marginRight: 6 }} />
            Filter shipments
          </div>

          <div className="hub-recon__filters">
            <div className="hub-recon__filter-field">
              <div className="hub-recon__filter-label">Status</div>
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder="Select one or more statuses"
                className="filter-select hub-recon__filter-control"
                style={{ width: "100%" }}
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_OPTIONS}
              />
            </div>

            <div className="hub-recon__filter-field hub-recon__filter-field--awb">
              <div className="hub-recon__filter-label">AWB Number</div>
              <Input
                value={awbNumber}
                onChange={(e) => setAwbNumber(e.target.value)}
                placeholder="Type full or partial AWB"
                allowClear
                prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                className="hub-recon__filter-control"
                bordered
                onPressEnter={handleApplyFilters}
                onKeyPress={(e) => {
                  const forbiddenChars = /[@!$#%^&*=+()""';?.><]/;
                  if (forbiddenChars.test(e.key)) {
                    e.preventDefault();
                    message.destroy();
                  }
                }}
              />
            </div>

            <div className="hub-recon__filter-actions">
              <div className="hub-recon__filter-label hub-recon__filter-label--spacer">&nbsp;</div>
              <div className="hub-recon__filter-actions-row">
                <Button
                  type="primary"
                  className="save-btn hub-recon__btn"
                  icon={<SearchOutlined />}
                  onClick={handleApplyFilters}
                >
                  Search
                </Button>
                <Button
                  className="hub-recon__btn hub-recon__btn--secondary"
                  icon={<ClearOutlined />}
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters && !statusFilter.length && !awbNumber}
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
          <div className="hub-recon__helper">
            Leave status empty to show all. Press Enter in AWB or click Search to apply filters.
          </div>

          <div className="hub-recon__stats">
            <button
              type="button"
              className={`hub-recon__stat available ${statusFilter.includes("AVAILABLE") ? "is-active" : ""}`}
              onClick={() => handleStatusCardClick("AVAILABLE")}
            >
              <div className="hub-recon__stat-label">Available</div>
              <div className="hub-recon__stat-value">{counts.availableCount}</div>
              <div className="hub-recon__stat-hint">Matched in inventory</div>
            </button>
            <button
              type="button"
              className={`hub-recon__stat overage ${statusFilter.includes("OVERAGE") ? "is-active" : ""}`}
              onClick={() => handleStatusCardClick("OVERAGE")}
            >
              <div className="hub-recon__stat-label">Overage</div>
              <div className="hub-recon__stat-value">{counts.overageCount}</div>
              <div className="hub-recon__stat-hint">Extra / unexpected</div>
            </button>
            <button
              type="button"
              className={`hub-recon__stat missing ${statusFilter.includes("MISSING") ? "is-active" : ""}`}
              onClick={() => handleStatusCardClick("MISSING")}
            >
              <div className="hub-recon__stat-label">Missing</div>
              <div className="hub-recon__stat-value">{counts.missingCount}</div>
              <div className="hub-recon__stat-hint">Not found in scan</div>
            </button>
            <button
              type="button"
              className={`hub-recon__stat excess-inscan ${statusFilter.includes("EXCESS_INSCAN") ? "is-active" : ""}`}
              onClick={() => handleStatusCardClick("EXCESS_INSCAN")}
            >
              <div className="hub-recon__stat-label">Excess Inscan</div>
              <div className="hub-recon__stat-value">{counts.excessInscanCount}</div>
              <div className="hub-recon__stat-hint">Not in the system</div>
            </button>
            <button
              type="button"
              className={`hub-recon__stat total ${!statusFilter.length ? "is-active" : ""}`}
              onClick={() => handleStatusCardClick("ALL")}
            >
              <div className="hub-recon__stat-label">Total</div>
              <div className="hub-recon__stat-value">{counts.totalCount}</div>
              <div className="hub-recon__stat-hint">Show all shipments</div>
            </button>
          </div>

          {hasActiveFilters && (
            <div className="hub-recon__active-filters">
              <span className="hub-recon__active-filters-label">Active filters:</span>
              {appliedFilters.status.map((status) => (
                <Tag
                  key={status}
                  color={STATUS_TAG[status]?.color || "default"}
                  closable
                  onClose={() => removeAppliedStatus(status)}
                >
                  {STATUS_TAG[status]?.label || status}
                </Tag>
              ))}
              {appliedFilters.awbNumber && (
                <Tag color="blue" closable onClose={removeAppliedAwb}>
                  AWB: {appliedFilters.awbNumber}
                </Tag>
              )}
              <Button type="link" size="small" onClick={handleClearFilters}>
                Clear all
              </Button>
            </div>
          )}
        </div>

        <div className="hub-recon__table-wrap">
          <TableContainer
            className="table-head-sticky"
            dataSource={data}
            columns={columns}
            rowKey={(record, index) => record.awbNumber || index}
            loading={{
              indicator: (
                <img
                  src={Smcs_Loader}
                  style={{ height: "100px", width: "100px" }}
                  alt="loading"
                />
              ),
              spinning: isFetching,
            }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    hasActiveFilters
                      ? "No shipments match the selected filters"
                      : "No shipments found for this reconciliation"
                  }
                />
              ),
            }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} shipments`,
            }}
            scroll={{ x: "max-content" }}
          />
        </div>
      </div>
    </div>
  );
};

export default HubReconciliationReportView;
